import React from 'react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useTranslation } from 'react-i18next';

const SITE_URL = 'https://monery.io';

interface PageSeo {
    title: string;
    description: string;
    image: string;
    path?: string;
    noindex?: boolean;
}

const pages: Record<string, PageSeo> = {
    '/terms-and-conditions': {
        title: 'Všeobecné obchodní podmínky | Monery',
        description: 'Všeobecné obchodní podmínky společnosti Monery Finance s.r.o.',
        image: 'home',
    },
    '/privacy-policy': {
        title: 'Ochrana osobních údajů | Monery',
        description: 'Ochrana osobních údajů společnosti Monery Finance s.r.o.',
        image: 'home',
    },
    '/marketing-communication-terms': {
        title: 'Souhlas se zasíláním obchodních sdělení | Monery',
        description: 'Souhlas se zasíláním obchodních sdělení společnosti Monery Finance s.r.o.',
        image: 'home',
    },

    '/': {
        title: 'Monery | Aplikace pro přehled investičního portfolia',
        description:
            'Sledujte akcie a ETF napříč brokery v aplikaci Monery. Importujte obchody a získejte přehled o výkonnosti portfolia, dividendách i podkladech k daním.',
        image: 'home',
    },
    '/pricing': {
        title: 'Ceník Monery Premium | Všechny funkce pro vaše portfolio',
        description:
            'Prohlédněte si cenu a funkce Monery Premium. Přehled portfolií, import obchodů, dividendy a daňové podklady. Vyzkoušejte si všechny funkce na 14 dní zdarma.',
        image: 'pricing',
    },
    '/faq': {
        title: 'Často kladené otázky | Monery',
        description:
            'Jak funguje Monery, jak importovat obchody a spravovat více portfolií? Najděte odpovědi na časté otázky k aplikaci, tarifu Premium a zákaznické podpoře.',
        image: 'faq',
    },
    '/contact': {
        title: 'Kontakt a podpora | Monery',
        description:
            'Potřebujete poradit s Monery nebo máte nápad na zlepšení? Kontaktujte podporu, přidejte se ke komunitě na Discordu nebo nám pošlete svůj návrh.',
        image: 'contact',
    },
    '/compound-interest': {
        path: '/kalkulacka-slozeneho-uroceni',
        title: 'Kalkulačka složeného úročení | Monery',
        description:
            'Spočítejte si budoucí hodnotu investic s pravidelnými vklady. Kalkulačka složeného úročení zohlední výnos, inflaci i daň a ukáže vývoj po jednotlivých letech.',
        image: 'calculator',
    },
    '/affiliate': {
        title: 'Affiliate program | Doporučte Monery',
        description:
            'Doporučujte Monery investorům a získávejte provize za přivedené klienty. Zjistěte, jak funguje affiliate program a sdílení vlastního doporučovacího odkazu.',
        image: 'affiliate',
        noindex: true,
    },
    '/affiliate/podminky': {
        title: 'Podmínky affiliate programu | Monery',
        description:
            'Podmínky zapojení do affiliate programu Monery. Dokument pro zájemce o doporučování aplikace a účastníky programu.',
        image: 'affiliate',
        noindex: true,
    },
    '/stan-se-partnerem': {
        title: 'Staňte se partnerem Monery | Přehled portfolií klientů',
        description:
            'Zjednodušte si přehled investičních portfolií svých klientů. Poznejte partnerský program Monery, jeho výhody a postup, jak se stát partnerem.',
        image: 'partner',
    },
    '/waiting-list': {
        title: 'Registrace na čekací listinu | Monery',
        description: 'Informace o registraci a aktivaci vašeho účtu Monery.',
        image: 'home',
        noindex: true,
    },
};

/** Public metadata is rendered on the server, including for social crawlers without JavaScript. */
export const WebSeo: React.FC = () => {
    const { pathname } = useRouter();
    const { t } = useTranslation();
    const page = pages[pathname];
    if (!page) return null;

    const canonical = `${SITE_URL}${page.path || pathname}`;
    const image = `${SITE_URL}/images/social-covers/${page.image}-hero.jpg`;
    const organizationId = `${SITE_URL}/#organization`;
    const websiteId = `${SITE_URL}/#website`;
    const pageId = `${canonical}#webpage`;
    const graph: Array<Record<string, unknown>> = [
        {
            '@type': 'Organization',
            '@id': organizationId,
            name: 'Monery',
            legalName: 'Monery Finance s.r.o.',
            url: `${SITE_URL}/`,
            logo: { '@type': 'ImageObject', url: `${SITE_URL}/images/social-covers/monery-logo.png` },
            sameAs: ['https://www.facebook.com/monery.io', 'https://www.instagram.com/monery.io'],
            address: {
                '@type': 'PostalAddress',
                streetAddress: 'Příčná 1892/4',
                addressLocality: 'Praha',
                postalCode: '110 00',
                addressCountry: 'CZ',
            },
        },
        {
            '@type': 'WebSite',
            '@id': websiteId,
            url: `${SITE_URL}/`,
            name: 'Monery',
            inLanguage: 'cs',
            publisher: { '@id': organizationId },
        },
        {
            '@type': pathname === '/contact' ? 'ContactPage' : 'WebPage',
            '@id': pageId,
            url: canonical,
            name: page.title,
            description: page.description,
            inLanguage: 'cs',
            isPartOf: { '@id': websiteId },
            primaryImageOfPage: { '@type': 'ImageObject', url: image, width: 1200, height: 630 },
        },
    ];
    if (pathname === '/faq') {
        const items = Object.values(t('pages.faq.items', { returnObjects: true })) as Array<{
            question: string;
            answer: string;
        }>;
        graph[2]['@type'] = 'FAQPage';
        graph[2].mainEntity = items.map((item) => ({
            '@type': 'Question',
            name: item.question.trim(),
            acceptedAnswer: { '@type': 'Answer', text: item.answer.trim() },
        }));
    }
    if (pathname === '/' || pathname === '/compound-interest') {
        graph.push({
            '@type': 'WebApplication',
            '@id': `${canonical}#application`,
            name: pathname === '/' ? 'Monery' : 'Kalkulačka složeného úročení',
            url: canonical,
            applicationCategory: 'FinanceApplication',
            operatingSystem: 'Web browser',
            inLanguage: 'cs',
            description: page.description,
            publisher: { '@id': organizationId },
            isPartOf: { '@id': pageId },
        });
    }
    return (
        <Head>
            <title>{page.title}</title>
            <meta name="description" content={page.description} key="description" />
            <meta
                name="robots"
                content={page.noindex ? 'noindex, follow' : 'index, follow, max-image-preview:large'}
                key="robots"
            />
            <link rel="canonical" href={canonical} key="canonical" />
            <meta property="og:type" content="website" key="og:type" />
            <meta property="og:site_name" content="Monery" key="og:site_name" />
            <meta property="og:locale" content="cs_CZ" key="og:locale" />
            <meta property="og:url" content={canonical} key="og:url" />
            <meta property="og:title" content={page.title} key="og:title" />
            <meta property="og:description" content={page.description} key="og:description" />
            <meta property="og:image" content={image} key="og:image" />
            <meta property="og:image:type" content="image/jpeg" key="og:image:type" />
            <meta property="og:image:width" content="1200" key="og:image:width" />
            <meta property="og:image:height" content="630" key="og:image:height" />
            <meta property="og:image:alt" content={page.title} key="og:image:alt" />
            <meta name="twitter:card" content="summary_large_image" key="twitter:card" />
            <meta name="twitter:title" content={page.title} key="twitter:title" />
            <meta name="twitter:description" content={page.description} key="twitter:description" />
            <meta name="twitter:image" content={image} key="twitter:image" />
            <meta name="twitter:image:alt" content={page.title} key="twitter:image:alt" />
            {!page.noindex && (
                <script
                    key="structured-data"
                    type="application/ld+json"
                    dangerouslySetInnerHTML={{
                        __html: JSON.stringify({ '@context': 'https://schema.org', '@graph': graph }).replace(
                            /</g,
                            '\\u003c',
                        ),
                    }}
                />
            )}
        </Head>
    );
};
